// src/pages/StudentDashboard.js
import React, { useState, useEffect, useContext } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import Navigation from '../components/Navigation';
import ProjectAcceptedModal from '../components/ProjectAcceptedModal';
import StudentPaymentInfo from '../components/StudentPaymentInfo';
import { setPageTitle } from '../utils/pageTitle';
import { leaveProject } from '../services/studentService';

const StudentDashboard = () => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const [applications, setApplications] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [studentProfile, setStudentProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAcceptedModal, setShowAcceptedModal] = useState(false);
  
  useEffect(() => {
    setPageTitle('Student Dashboard');
    
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Get student profile
        const profileRef = doc(db, 'studentProfiles', currentUser.uid);
        const profileDoc = await getDoc(profileRef);
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          console.log('Student profile data:', profileData); // Debug log
          setStudentProfile(profileData);
        } else {
          console.log('No student profile found for:', currentUser.uid); // Debug log
        }
        
        // Get applications - SIMPLE QUERY
        const applicationsQuery = query(
          collection(db, 'applications'),
          where('studentId', '==', currentUser.uid),
          orderBy('appliedAt', 'desc')
        );
        
        const applicationsSnapshot = await getDocs(applicationsQuery);
        let applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // For accepted applications, verify student is still enrolled
        applicationsData = await Promise.all(
          applicationsData.map(async (app) => {
            if (app.status === 'accepted') {
              const projectRef = doc(db, 'projects', app.projectId);
              const projectSnap = await getDoc(projectRef);
              if (!projectSnap.exists() || !projectSnap.data().enrolledStudents?.includes(currentUser.uid)) {
                // If student is not enrolled, mark application as 'left'
                await updateDoc(doc(db, 'applications', app.id), {
                  status: 'left',
                  leftAt: new Date().toISOString(),
                  updatedAt: serverTimestamp()
                });
                return { ...app, status: 'left' };
              }
            }
            return app;
          })
        );
        
        setApplications(applicationsData);
        
        // Check for newly accepted applications - SIMPLE ACCESS
        const recentlyAccepted = applicationsData.find(app => 
          app.status === 'accepted' && 
          !app.modalShown &&
          app.acceptedAt && 
          new Date() - new Date(app.acceptedAt) < 7 * 24 * 60 * 60 * 1000
        );
        
        if (recentlyAccepted) {
          // Get project details
          const projectRef = doc(db, 'projects', recentlyAccepted.projectId);
          const projectSnap = await getDoc(projectRef);
          
          if (projectSnap.exists()) {
            const projectData = projectSnap.data();
            // Only set as active if student is still in enrolledStudents
            if (projectData.enrolledStudents?.includes(currentUser.uid)) {
              setActiveProject({
                id: recentlyAccepted.projectId,
                ...projectData,
                applicationId: recentlyAccepted.id
              });
              
              setShowAcceptedModal(true);
              
              // Mark modal as shown
              const applicationRef = doc(db, 'applications', recentlyAccepted.id);
              await updateDoc(applicationRef, {
                modalShown: true
              });
            }
          }
        }
        
        // Get active project from accepted applications
        const acceptedApplication = applicationsData.find(app => app.status === 'accepted');
        
        if (acceptedApplication) {
          const projectRef = doc(db, 'projects', acceptedApplication.projectId);
          const projectSnap = await getDoc(projectRef);
          
          if (projectSnap.exists()) {
            const projectData = projectSnap.data();
            // Only set as active if student is still in enrolledStudents
            if (projectData.enrolledStudents?.includes(currentUser.uid)) {
              setActiveProject({
                id: acceptedApplication.projectId,
                ...projectData,
                applicationId: acceptedApplication.id
              });
            }
          }
        } else {
          // No accepted applications or not enrolled, clear active project
          setActiveProject(null);
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Failed to load dashboard data. Please try again.');
        setLoading(false);
      }
    };
    
    if (currentUser) {
      fetchDashboardData();
    }
  }, [currentUser]);
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    if (timestamp.seconds) {
      return new Date(timestamp.seconds * 1000).toLocaleDateString();
    } else {
      return new Date(timestamp).toLocaleDateString();
    }
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'accepted':
        return { 
          background: 'rgba(34, 197, 94, 0.1)', 
          color: 'var(--success)' 
        };
      case 'pending':
        return { 
          background: 'rgba(251, 191, 36, 0.1)', 
          color: 'var(--warning)' 
        };
      case 'rejected':
        return { 
          background: 'rgba(239, 68, 68, 0.1)', 
          color: 'var(--danger)' 
        };
      case 'withdrawn':
      case 'left':
        return { 
          background: 'rgba(156, 163, 175, 0.1)', 
          color: '#6b7280' 
        };
      default:
        return {};
    }
  };

  const currentApplication = applications.length > 0 ? applications[0] : null;
  const completedProjects = applications.filter(app => app.status === 'left').length;
  
  const handleModalContinue = () => {
    setShowAcceptedModal(false);
    // Navigate to the actual active project
    if (activeProject) {
      navigate(`/student/project/${activeProject.id}`);
    }
  };
  
  const handleWithdrawApplication = async (application) => {
    if (window.confirm('Are you sure you want to withdraw this application? This action cannot be undone.')) {
      try {
        await updateDoc(doc(db, 'applications', application.id), {
          status: 'withdrawn',
          withdrawnAt: new Date().toISOString()
        });
        
        // Refresh applications
        const applicationsQuery = query(
          collection(db, 'applications'),
          where('studentId', '==', currentUser.uid),
          orderBy('appliedAt', 'desc')
        );
        
        const applicationsSnapshot = await getDocs(applicationsQuery);
        const applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setApplications(applicationsData);
        
        alert('Application withdrawn successfully.');
      } catch (error) {
        console.error('Error withdrawing application:', error);
        alert('Failed to withdraw application. Please try again.');
      }
    }
  };

  const handleLeaveProject = async () => {
    if (window.confirm('Are you sure you want to leave this project? This action cannot be undone.')) {
      try {
        await leaveProject(activeProject.id, currentUser.uid);
        
        // Clear active project immediately
        setActiveProject(null);
        
        // Refresh all dashboard data to ensure consistent state
        const [applicationsSnapshot, projectDoc] = await Promise.all([
          // Get updated applications
          getDocs(query(
            collection(db, 'applications'),
            where('studentId', '==', currentUser.uid),
            orderBy('appliedAt', 'desc')
          )),
          // Get updated project data
          getDoc(doc(db, 'projects', activeProject.id))
        ]);

        // Update applications state
        const applicationsData = applicationsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setApplications(applicationsData);

        // Double check project enrollment status
        const projectData = projectDoc.exists() ? projectDoc.data() : null;
        if (projectData && !projectData.enrolledStudents?.includes(currentUser.uid)) {
          setActiveProject(null); // Ensure project is cleared if student is not enrolled
        }
        
        alert('You have successfully left the project.');
      } catch (error) {
        console.error('Error leaving project:', error);
        alert('Failed to leave project. Please try again.');
      }
    }
  };
  
  if (loading) {
    return (
      <>
        <Navigation />
        <div className="container" style={{ padding: '60px 0', textAlign: 'center' }}>
          Loading your dashboard...
        </div>
      </>
    );
  }
  
  if (error) {
    return (
      <>
        <Navigation />
        <div className="container" style={{ padding: '60px 0', textAlign: 'center', color: 'var(--danger)' }}>
          {error}
        </div>
      </>
    );
  }
  
  return (
    <div>
      <Navigation />
      
      <div className="container" style={{ padding: '80px 0' }}>
        {error && (
          <div className="error-message" style={{ color: 'red', marginBottom: '20px' }}>
            {error}
          </div>
        )}
        
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '32px' 
        }}>
          <div>
            <h1 className="h1 mb-2">Student Dashboard</h1>
            <p className="text-secondary text-lg mb-0">Manage your project and application status</p>
          </div>
          <Link 
            to="/projects" 
            className="btn btn-primary"
          >
            Browse Projects
          </Link>
        </div>
        
        {/* Main Content */}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px' }}>
          <div>
            {/* Current Status Section */}
            {activeProject ? (
              <div style={{ 
                backgroundColor: '#f0f9ff',
                padding: '32px',
                borderRadius: '16px',
                marginBottom: '32px',
                border: '1px solid #e0f2fe'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                  <div style={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#22c55e',
                    marginRight: '8px'
                  }}></div>
                  <span style={{ color: '#22c55e', fontWeight: 'bold', fontSize: '14px' }}>Active Project</span>
                </div>
                
                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  margin: '0 0 16px 0',
                  color: '#1a1a1a'
                }}>
                  {activeProject.title}
                </h2>
                
                <p style={{ 
                  color: '#666', 
                  fontSize: '16px', 
                  lineHeight: '1.5',
                  margin: '0 0 20px 0'
                }}>
                  {activeProject.description && activeProject.description.length > 150
                    ? `${activeProject.description.substring(0, 150)}...`
                    : activeProject.description}
                </p>
                
                <div style={{ 
                  display: 'flex', 
                  gap: '24px', 
                  marginBottom: '24px',
                  fontSize: '14px',
                  color: '#666'
                }}>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Company:</span> {activeProject.companyName}
                  </div>
                  <div>
                    <span style={{ fontWeight: 'bold' }}>Duration:</span> 1-2 weeks
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  <Link 
                    to={`/student/project/${activeProject.id}`}
                    className="btn btn-primary"
                  >
                    Go to Project Portal
                  </Link>
                  <button
                    onClick={handleLeaveProject}
                    className="btn btn-outline-danger"
                  >
                    Leave Project
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ 
                backgroundColor: '#f9fafb',
                padding: '32px',
                borderRadius: '16px',
                marginBottom: '32px',
                textAlign: 'center'
              }}>
                <h2 style={{ 
                  fontSize: '24px', 
                  fontWeight: 'bold', 
                  margin: '0 0 8px 0',
                  color: '#1a1a1a'
                }}>
                  Current Status
                </h2>
                <p style={{ color: '#666', marginBottom: '24px' }}>No active project</p>
                <Link 
                  to="/projects"
                  style={{
                    backgroundColor: '#6366f1',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    textDecoration: 'none',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  Browse Projects
                </Link>
              </div>
            )}
            
            {/* Application History */}
            <div style={{
              backgroundColor: 'white',
              padding: '32px',
              borderRadius: '16px',
              marginBottom: '32px'
            }}>
              <h2 style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: '0 0 24px 0',
                color: '#1a1a1a'
              }}>
                Project History
              </h2>
              
              {/* Show completed/past projects */}
              {applications.filter(app => app.status === 'left' || app.status === 'rejected').length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px 20px',
                  color: '#666' 
                }}>
                  <p>No project history yet</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {applications.filter(app => app.status === 'left' || app.status === 'rejected').map((application) => (
                    <div 
                      key={application.id}
                      style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '20px 0',
                        borderBottom: '1px solid #f3f4f6'
                      }}
                    >
                      <div>
                        <h4 style={{ 
                          margin: '0 0 4px 0', 
                          fontSize: '16px',
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}>
                          {application.projectTitle}
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          fontSize: '14px',
                          color: '#666'
                        }}>
                          {application.status === 'left' ? 'Left' : 'Completed'} on {formatDate(application.leftAt || application.rejectedAt || application.appliedAt)}
                        </p>
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          backgroundColor: application.status === 'left' ? '#6b7280' : '#ef4444',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          textTransform: 'capitalize'
                        }}>
                          {application.status === 'left' ? 'Left' : 'Rejected'}
                        </span>
                        
                        <Link 
                          to={`/projects/${application.projectId}`}
                          style={{
                            color: '#6366f1',
                            textDecoration: 'none',
                            fontSize: '14px',
                            fontWeight: 'bold'
                          }}
                        >
                          View Project
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          {/* Right Sidebar */}
          <div>
            {/* Profile Section */}
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '16px',
              marginBottom: '24px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
                <div style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '50%',
                  backgroundColor: '#f3f4f6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '16px',
                  overflow: 'hidden',
                  flexShrink: 0
                }}>
                  {(studentProfile?.profilePicture || studentProfile?.photoURL) ? (
                    <img 
                      src={studentProfile.profilePicture || studentProfile.photoURL} 
                      alt={`${studentProfile.firstName || studentProfile.fullName || ''} ${studentProfile.lastName || ''}`} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : (
                    <span style={{ fontSize: '24px', fontWeight: 'bold', color: '#666' }}>
                      {(studentProfile?.firstName?.charAt(0) || studentProfile?.fullName?.charAt(0) || currentUser?.email?.charAt(0) || 'S').toUpperCase()}
                    </span>
                  )}
                </div>
                
                <div>
                  <h3 style={{ 
                    fontSize: '20px', 
                    fontWeight: 'bold', 
                    margin: '0 0 4px 0',
                    color: '#1a1a1a'
                  }}>
                    {studentProfile ? 
                      (studentProfile.fullName || 
                       `${studentProfile.firstName || ''} ${studentProfile.lastName || ''}`.trim() ||
                       currentUser?.email?.split('@')[0] || 
                       'Student') 
                      : 'Loading...'}
                  </h3>
                  
                  <p style={{ 
                    color: '#666', 
                    fontSize: '14px',
                    margin: 0
                  }}>
                    {studentProfile?.university || studentProfile?.college || 'University not set'}
                  </p>
                </div>
              </div>
              
              <Link 
                to="/student/profile"
                style={{
                  backgroundColor: 'transparent',
                  color: '#6366f1',
                  border: '2px solid #6366f1',
                  padding: '8px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'inline-block'
                }}
              >
                View Profile
              </Link>
            </div>
            
            {/* Quick Stats */}
            <div style={{
              backgroundColor: 'white',
              padding: '24px',
              borderRadius: '16px'
            }}>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: 'bold', 
                margin: '0 0 16px 0',
                color: '#1a1a1a'
              }}>
                Quick Stats
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Applications Sent</span>
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    color: '#6366f1'
                  }}>
                    {applications.length}
                  </span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '14px', color: '#666' }}>Projects Completed</span>
                  <span style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    color: '#6366f1'
                  }}>
                    {completedProjects}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {showAcceptedModal && activeProject && (
        <ProjectAcceptedModal 
          project={activeProject}
          onContinue={handleModalContinue}
        />
      )}
    </div>
  );
};

export default StudentDashboard;